// Third party
import React from 'react';
import classNames from 'classnames';

// Namespaces
import * as ProposalNS from '@/modules/proposal/Proposal';

// Components
import { Checkbox, ToggleSwitch } from '@/views/components/inputs';
import InfoTooltip from '@/views/components/tooltips/InfoTooltip';
import ProposalItemListerOption from './ProposalItemListerOption';
import { ReactComponent as PurposeFlagCurrentIcon } from '@/images/icons/comparison-item-purpose-flag-current.svg?react';
import { ReactComponent as PurposeFlagRecommendedIcon } from '@/images/icons/comparison-item-purpose-flag-recommended.svg?react';
import { ReactComponent as PurposeFlagNoneIcon } from '@/images/icons/comparison-item-purpose-flag-none.svg?react';

// Utils
import { formatMessage, FormattedMessage } from '@/utils/i18n';
import { userHasPermissionEnabled, PERMISSIONS } from '@/utils/permissions';
import { COMPARISON_ITEM_TYPES } from '@/utils/constants';
import { BMOi18n } from '@/utils/bmo-gif/constants';
import { SelectItem } from '@/views/components/inputs/Select';
import { PROPOSAL_ITEM_PURPOSE } from '../../../../utils/constants';
import { DropItemPosition } from '@/utils/drag-and-drop';

import Locale from '@/types/locale';
import Comparison from '@/types/comparison';
import ComparisonItem from '@/types/comparison-item';
import ProposalItem from '@/types/proposal-item';
import User from '@/types/user';
import BMOGIFItem from '@/types/bmo-gif/bmo-gif-item';
import { ComparisonItemCategory } from '@/redux/modules/types/comparison';

type Props = {
  user: User;
  locale: Locale;
  className?: string;
  comparison: Comparison;
  proposal: ProposalNS.Proposal;
  allowedItemTypes: ProposalNS.ProposalAllowedItemType[];
  maxItemsNumber: number;
  gifHideBaseScenario?: boolean;
  showIncludeFundProfiles?: boolean;
  showIncludePortfolioProfiles?: boolean;
  showIncludeGroupProfiles?: boolean;
  showShortHistory?: boolean;
  showProposalItemPurposeSelector?: boolean;
  itemColorOverride?: string[];
  onItemsSelection: (items: ComparisonItem[]) => void;
  onItemPurposeFlagUpdate: (itemId: string, flags: string[]) => void;
  onGifItemsSelection: (items: BMOGIFItem[]) => void;
  onToggleGifBaseScenario: (value: boolean) => void;
  onToggleFundProfiles: (value: boolean) => void;
  onTogglePortfolioProfiles: (value: boolean) => void;
  onToggleGroupProfiles: (value: boolean) => void;
  onToggleShortHistory: (value: boolean) => void;
  onChangeItemOrder: (itemId: string, referenceId: string, position?: DropItemPosition) => void;
};

class ProposalItemLister extends React.PureComponent<Props> {
  hasRebalanceAnalysisSection: boolean = false;

  constructor(props: Props) {
    super(props);

    this.hasRebalanceAnalysisSection = this.props.proposal.template.root.some(this.checkForRebalanceAnalysisSection);
  }

  componentDidUpdate(prevProps: Props) {
    if (prevProps.proposal.template.key !== this.props.proposal.template.key) {
      this.hasRebalanceAnalysisSection = this.props.proposal.template.root.some(this.checkForRebalanceAnalysisSection);
    }
  }

  /** Checks if this proposal template has Rebalance Analysis somewhere in the section tree. For use as an override
   * in templates that don't have `showProposalItemPurposeSelector` set to `true` but have Rebalance Analysis as
   * to not create an impossible state where Rebalance Analysis is unusable.
   */
  checkForRebalanceAnalysisSection = (division: ProposalNS.ProposalTemplateDivisionElement): boolean =>
    division.sections.some(section => section.key === ProposalNS.DAOType.RebalanceAnalysis);

  onItemToggle = (item: ProposalItem) => {
    const {
      proposal: { items },
    } = this.props;
    const { comparison, allowedItemTypes } = this.props;

    const isSelected = items.some(i => i._id === item._id);
    let newItems: ComparisonItem[] = [];

    if (isSelected) {
      newItems = items.slice();
      newItems = newItems.filter(i => i._id !== item._id);
    } else {
      // insert it back at the right position
      const filteredItems = comparison.items.filter(compItem => allowedItemTypes.indexOf(compItem.type) !== -1);

      for (const filteredItem of filteredItems) {
        if (filteredItem._id === item._id || items.find(it => it._id === filteredItem._id) !== undefined) {
          newItems.push({ ...filteredItem, isFX: false, flags: items.find(i => i._id === filteredItem._id)?.flags ?? [] });
        }
      }
    }

    this.props.onItemsSelection(newItems);
  };

  onGifItemToggle = (item: BMOGIFItem) => {
    const {
      proposal: { gifItems },
    } = this.props;
    const { comparison } = this.props;

    if (gifItems === undefined) {
      return;
    }

    const isSelected = gifItems.some(i => i._id === item._id);
    let newItems: BMOGIFItem[] = [];

    if (isSelected) {
      newItems = gifItems.slice();
      newItems = newItems.filter(i => i._id !== item._id);
    } else {
      // insert it back at the right position
      const filteredItems = comparison.gifItems !== undefined ? [...comparison.gifItems] : [];

      for (const filteredItem of filteredItems) {
        if (filteredItem._id === item._id || gifItems.find(it => it._id === filteredItem._id) !== undefined) {
          newItems.push({ ...filteredItem });
        }
      }
    }

    this.props.onGifItemsSelection(newItems);
  };

  onToggleGifBaseScenario = (toggled: boolean) => {
    this.props.onToggleGifBaseScenario(toggled);
  };

  itemIsSelected = (itemId: string) => {
    const { proposal } = this.props;

    return proposal.items.some(i => i._id === itemId);
  };

  gifItemIsSelected = (itemId: string) => {
    const { proposal } = this.props;

    return proposal.gifItems !== undefined && proposal.gifItems.some(i => i._id === itemId);
  };

  itemIsDisabled = (item: ComparisonItem) => {
    const { maxItemsNumber, proposal } = this.props;

    if (item.category === ComparisonItemCategory.CURRENT || item.category === ComparisonItemCategory.RECOMMENDED) {
      return true;
    }

    if (proposal.items.length < maxItemsNumber) {
      return false;
    }

    return !this.itemIsSelected(item._id);
  };

  handleChangeItemPurpose = (item: ComparisonItem, selectedItem?: SelectItem) => {
    if (!selectedItem) {
      return;
    }

    // Remove any existing purpose flags
    const flags = item.flags?.filter(flag => !Object.values<string>(PROPOSAL_ITEM_PURPOSE).includes(flag)) ?? [];

    // Push new flag, only if its not "None"
    if (selectedItem.value !== PROPOSAL_ITEM_PURPOSE.NONE) {
      flags.push(selectedItem.value);
    }

    this.props.onItemPurposeFlagUpdate(item._id, flags);
  };

  handleChangeItemOrder = (itemId: string, referenceId: string, position?: DropItemPosition) => {
    this.props.onChangeItemOrder(itemId, referenceId, position);
  };

  getItemPurposeList = (): SelectItem[] => {
    const { items } = this.props.proposal;

    const icons = {
      [PROPOSAL_ITEM_PURPOSE.NONE]: PurposeFlagNoneIcon,
      [PROPOSAL_ITEM_PURPOSE.CURRENT]: PurposeFlagCurrentIcon,
      [PROPOSAL_ITEM_PURPOSE.RECOMMENDED]: PurposeFlagRecommendedIcon,
    };

    return Object.entries(PROPOSAL_ITEM_PURPOSE).map(([key, value]) => {
      const Icon = icons[value || PROPOSAL_ITEM_PURPOSE.NONE];

      let disabled = false;
      switch (value) {
        case PROPOSAL_ITEM_PURPOSE.CURRENT: {
          disabled = items.some(item => item.flags?.includes(value));
          break;
        }
        case PROPOSAL_ITEM_PURPOSE.RECOMMENDED: {
          const recommendedRequirement = this.props.proposal.template.requirements.find(
            req => req.key === ProposalNS.TemplateRequirementKeys.MaxItemPurpose,
          ) as ProposalNS.ProposalTemplateMaxItemPurposeRequirement | undefined;

          disabled = recommendedRequirement ? items.filter(item => item.flags?.includes(value)).length >= recommendedRequirement.max : false;
          break;
        }
      }

      return {
        value,
        label: (
          <div className="d-flex align-items-center">
            <Icon className="proposal-item-purpose-select-icon" aria-hidden="true" />
            <span>{formatMessage(`Proposal.ProposalItemPurpose.${key}`)}</span>
          </div>
        ),
        disabled,
      };
    });
  };

  renderGifItem = (items?: BMOGIFItem[]) => {
    const { gifHideBaseScenario, locale } = this.props;

    if (!items || items.length === 0) {
      return null;
    }

    const item = items[0];
    const itemIsSelected = this.gifItemIsSelected(item._id);
    const showBaseScenarioToggle = items.some(gifItem => gifItem.scenarios.length > 0);

    const outItem = (
      <div className="proposal-checklist-option" onClick={() => this.onGifItemToggle(item)}>
        <Checkbox label={BMOi18n[locale].PROPOSAL.GIF_ILLUSTRATION} selected={itemIsSelected} />
      </div>
    );

    const outToggle = showBaseScenarioToggle ? (
      <div>
        <ToggleSwitch className="visualize-option" checked={gifHideBaseScenario} onChange={this.onToggleGifBaseScenario}>
          <FormattedMessage id="Proposal.HideBaseScenario" />
        </ToggleSwitch>
      </div>
    ) : null;

    return (
      <div className="gif-item-selector">
        {outItem}
        {outToggle}
      </div>
    );
  };

  render() {
    const {
      comparison,
      allowedItemTypes,
      className,
      maxItemsNumber,
      user,
      proposal,
      showIncludeFundProfiles,
      showIncludePortfolioProfiles,
      showIncludeGroupProfiles,
      showShortHistory,
      showProposalItemPurposeSelector,
      itemColorOverride,
    } = this.props;

    const filteredItems = comparison.items.filter(item => allowedItemTypes.indexOf(item.type) !== -1);

    const hasShortItems = comparison.items.some(item => ([COMPARISON_ITEM_TYPES.PORTFOLIO, COMPARISON_ITEM_TYPES.BENCHMARK] as string[]).includes(item.type));

    const hasFunds = proposal.items.some(item => item.type === COMPARISON_ITEM_TYPES.FUND);
    const hasPortfolios = proposal.items.some(item => item.type === COMPARISON_ITEM_TYPES.PORTFOLIO);
    const hasGroups = proposal.items.some(item => item.type === COMPARISON_ITEM_TYPES.GROUP);

    const showProfilesContainer = proposal.items.length > 0 && (showIncludeFundProfiles || showIncludePortfolioProfiles || showIncludeGroupProfiles);

    let shortHistoryMessage = formatMessage('Proposal.ShowLongerHistory.Disclaimer');
    if (proposal.readOnly) {
      shortHistoryMessage += ` ${formatMessage('Comparison.ReadOnlySettingsTooltip')}`;
    }

    return (
      <>
        <div className="proposal-items-container" data-testid="proposal-items-container">
          {this.renderGifItem(comparison.gifItems)}

          {filteredItems.length > 0 && (
            <div className="max-items">
              <FormattedMessage id="Proposal.MaxItems" values={{ max: maxItemsNumber }} />
            </div>
          )}

          <div className="items-list-container">
            {filteredItems.map((item: ComparisonItem) => (
              <ProposalItemListerOption
                key={item._id}
                item={item}
                showIcon
                proposal={proposal}
                user={user}
                showProposalItemPurposeSelector={showProposalItemPurposeSelector || this.hasRebalanceAnalysisSection}
                disabled={this.itemIsDisabled(item)}
                selected={this.itemIsSelected(item._id)}
                itemPurposeList={this.getItemPurposeList()}
                onToggle={this.onItemToggle}
                onChangeItemPurpose={this.handleChangeItemPurpose}
                onItemPurposeFlagUpdate={this.props.onItemPurposeFlagUpdate}
                onChangeItemOrder={this.handleChangeItemOrder}
              />
            ))}
          </div>

          <div className="toggle-options">
            {hasShortItems && showShortHistory && userHasPermissionEnabled(user, PERMISSIONS.SHORT_HISTORY) && (
              <ToggleSwitch
                className="visualize-option"
                checked={comparison.shortHistory}
                onChange={this.props.onToggleShortHistory}
                disabled={!hasPortfolios || proposal.readOnly}
                tooltip={<InfoTooltip title={formatMessage('Proposal.ShowLongerHistory.Title')} message={shortHistoryMessage} />}
              >
                <FormattedMessage id="Proposal.ShowLongerHistory.Title" />
              </ToggleSwitch>
            )}
          </div>
        </div>

        {showProfilesContainer && (
          <div className="profiles-container">
            <h4>
              <FormattedMessage id="Proposal.ProfilesTitle" />
            </h4>

            {showIncludeFundProfiles && (
              <ToggleSwitch
                className="visualize-option"
                onChange={this.props.onToggleFundProfiles}
                disabled={!hasFunds}
                checked={hasFunds && proposal.options.includeFundProfiles}
                tooltip={
                  <InfoTooltip
                    title={formatMessage('Proposal.IncludeFundProfiles.Title')}
                    message={formatMessage('Proposal.IncludeFundProfiles.Disclaimer')}
                    data-private
                    data-dd-privacy="mask"
                  />
                }
              >
                <FormattedMessage id="Proposal.IncludeFundProfiles.Title" />
              </ToggleSwitch>
            )}

            {showIncludePortfolioProfiles && (
              <ToggleSwitch
                className="visualize-option"
                onChange={this.props.onTogglePortfolioProfiles}
                disabled={!hasPortfolios}
                checked={hasPortfolios && proposal.options.includePortfolioProfiles}
                tooltip={
                  <InfoTooltip
                    title={formatMessage('Proposal.IncludePortfolioProfiles.Title')}
                    message={formatMessage('Proposal.IncludePortfolioProfiles.Disclaimer')}
                    data-private
                    data-dd-privacy="mask"
                  />
                }
              >
                <FormattedMessage id="Proposal.IncludePortfolioProfiles.Title" />
              </ToggleSwitch>
            )}

            {showIncludeGroupProfiles && (
              <ToggleSwitch
                className="visualize-option"
                onChange={this.props.onToggleGroupProfiles}
                disabled={!hasGroups}
                checked={hasGroups && proposal.options.includeGroupProfiles}
                tooltip={
                  <InfoTooltip
                    title={formatMessage('Proposal.IncludeGroupProfiles.Title')}
                    message={formatMessage('Proposal.IncludeGroupProfiles.Disclaimer')}
                    data-private
                    data-dd-privacy="mask"
                  />
                }
              >
                <FormattedMessage id="Proposal.IncludeGroupProfiles.Title" />
              </ToggleSwitch>
            )}
          </div>
        )}
      </>
    );
  }
}

export default ProposalItemLister;
